import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { FiSliders, FiX } from 'react-icons/fi';
import MsvSelect from '../MsvSelect/MsvSelect';
import { useAnimatedPresence } from '../../hooks/useAnimatedPresence';
import {
    driverIdPairs,
    yearsIdPairs,
    seriesIdPairs,
} from '../../constants/dropdownIdPairs';

const lockedInputStyle = {
    backgroundColor: '#f0f0f0',
    opacity: 0.6,
    cursor: 'not-allowed',
};

const findBy = (options, value, key) => {
    if (!value || !Array.isArray(options)) return null;
    return options.find((o) => o[key] === value) || null;
};

/**
 * Shared hero-card filter toolbar: Driver, Series, Sort, Filters (mobile).
 * Changes apply immediately; year lives in the mobile drawer.
 */
const VaultHeroFilterForm = ({
    formData,
    onFiltersApply,
    sortBy = 'recent',
    onSortChange,
    onClearFilters,
    className = '',
    children,
    lockDriverSeries = false,
}) => {
    const data = {
        driverName: '',
        series: '',
        year: '',
        ...(formData || {}),
    };

    const [showFilters, setShowFilters] = useState(false);
    const { mounted: filtersMounted, visible: filtersVisible } = useAnimatedPresence(showFilters);

    const [drivers, setDrivers] = useState([]);
    const [series, setSeries] = useState([]);
    const [years, setYears] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    useEffect(() => {
        setDrivers([...driverIdPairs].sort((a, b) =>
            a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
        ));
        setSeries([...seriesIdPairs].sort((a, b) =>
            a.seriesName.toLowerCase().localeCompare(b.seriesName.toLowerCase())
        ));
        setYears([...yearsIdPairs].sort((a, b) =>
            String(b.name).localeCompare(String(a.name))
        ));
    }, []);

    useEffect(() => {
        setSelectedDriver(findBy(drivers, data.driverName, 'fullName'));
        setSelectedSeries(findBy(series, data.series, 'seriesName'));
        setSelectedYear(findBy(years, data.year, 'name'));
    }, [data.driverName, data.series, data.year, drivers, series, years]);

    const patchForm = (patch) => onFiltersApply?.({ ...data, ...patch });

    const drawerFilterCount = [data.year].filter(Boolean).length;

    const handleClearFilters = (e) => {
        e?.preventDefault?.();
        setSelectedDriver(null);
        setSelectedSeries(null);
        setSelectedYear(null);
        const cleared = { driverName: '', series: '', year: '' };
        onClearFilters?.(cleared);
        setShowFilters(false);
    };

    return (
        <>
            <div className={`msv-browse-filter-form ${className}`.trim()}>
                <div className="msv-browse-filter-row">
                    <Autocomplete
                        disablePortal
                        options={drivers}
                        getOptionLabel={(option) => option.fullName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedDriver}
                        onChange={(e, val) => {
                            setSelectedDriver(val);
                            if (lockDriverSeries && val) setSelectedSeries(null);
                            patchForm({
                                driverName: val?.fullName || '',
                                ...(lockDriverSeries && val ? { series: '' } : {}),
                            });
                        }}
                        disabled={lockDriverSeries && selectedSeries !== null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Driver"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    style: lockDriverSeries && selectedSeries ? lockedInputStyle : {},
                                }}
                            />
                        )}
                        sx={{ width: '100%', marginRight: 0 }}
                    />

                    <Autocomplete
                        disablePortal
                        options={series}
                        getOptionLabel={(option) => option.seriesName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedSeries}
                        onChange={(e, val) => {
                            setSelectedSeries(val);
                            if (lockDriverSeries && val) setSelectedDriver(null);
                            patchForm({
                                series: val?.seriesName || '',
                                ...(lockDriverSeries && val ? { driverName: '' } : {}),
                            });
                        }}
                        disabled={lockDriverSeries && selectedDriver !== null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Series"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    style: lockDriverSeries && selectedDriver ? lockedInputStyle : {},
                                }}
                            />
                        )}
                        sx={{ width: '100%', marginRight: 0 }}
                    />

                    <MsvSelect
                        id="msv-vault-hero-sort"
                        label="Sort"
                        className="is-row is-compact msv-sort-field"
                        value={sortBy === 'oldest' ? 'oldest' : 'recent'}
                        options={[
                            { value: 'recent', label: 'Recently added' },
                            { value: 'oldest', label: 'Oldest first' },
                        ]}
                        onChange={(next) => onSortChange?.(next === 'oldest' ? 'oldest' : 'recent')}
                    />

                    <button
                        type="button"
                        className={`msv-browse-filters-btn${showFilters || drawerFilterCount ? ' is-active' : ''}`}
                        onClick={() => setShowFilters(true)}
                    >
                        <FiSliders aria-hidden />
                        Filters{drawerFilterCount ? ` · ${drawerFilterCount}` : ''}
                    </button>
                </div>
                {children}
            </div>

            {filtersMounted && (
                <div className={`msv-browse-drawer-root${filtersVisible ? ' is-open' : ''}`}>
                    <button
                        type="button"
                        className="msv-browse-drawer-backdrop"
                        aria-label="Close filters"
                        onClick={() => setShowFilters(false)}
                    />
                    <aside className="msv-browse-drawer" role="dialog" aria-label="Filters">
                        <div className="msv-browse-drawer-header">
                            <h2>Filters</h2>
                            <button
                                type="button"
                                className="msv-browse-drawer-close"
                                onClick={() => setShowFilters(false)}
                                aria-label="Close"
                            >
                                <FiX />
                            </button>
                        </div>

                        <div className="msv-browse-drawer-body">
                            <Autocomplete
                                options={years}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={selectedYear}
                                onChange={(e, val) => {
                                    setSelectedYear(val);
                                    patchForm({ year: val?.name || '' });
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Year" size="small" />
                                )}
                            />
                        </div>

                        <div className="msv-browse-drawer-footer">
                            <button
                                type="button"
                                className="msv-browse-search-btn msv-browse-clear-btn"
                                onClick={handleClearFilters}
                            >
                                Clear filters
                            </button>
                            <button
                                type="button"
                                className="msv-browse-search-btn"
                                onClick={() => setShowFilters(false)}
                            >
                                Done
                            </button>
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
};

export default VaultHeroFilterForm;
